import { motion } from 'framer-motion'
import founderPhoto from '../assets/founder-photo.webp'
import { duration, stagger } from '../lib/motion'

// Merged from the former separate WhyUs + AboutFounder sections — both were
// answering "why should I trust you specifically," just via two different
// proofs (positioning copy, then founder story) three scrolls apart. One
// section, one trust moment: philosophy → the six concrete reasons → the
// person actually behind it.

const POINTS = [
  { title: 'Trusted in seconds', copy: 'Visitors decide in seconds whether to take you seriously. We design for that second.' },
  { title: 'Built to convert', copy: 'Every layout choice removes friction between interest and enquiry.' },
  { title: 'Meets people on their phone', copy: 'Most of your customers will find you on mobile. So does your site, first.' },
  { title: 'Loads before they leave', copy: 'A slow site loses customers before they ever see what you offer.' },
  { title: 'Built to be found', copy: 'Structured with keyword-informed content and on-page SEO from day one, not bolted on after — so search engines can actually surface you.' },
  { title: 'Yours to update', copy: 'You should be able to update your own site without calling us.' },
]

export default function WhyUs() {
  return (
    <section id="about" className="py-24 border-t" style={{ borderColor: 'var(--border)' }}>
      <div className="container-bw">
        <div className="max-w-2xl mb-14">
          <p className="eyebrow mb-4">Beyond Code</p>
          <h2 className="font-display text-3xl md:text-4xl mb-4" style={{ color: 'var(--text-primary)' }}>
            Every layout choice is a decision your visitor doesn't notice making.
            It still decides what they do next.
          </h2>
          <p style={{ color: 'var(--text-secondary)' }}>
            Not another agency making pretty websites. We think about why people trust a
            business, what makes them hesitate, and how the website can change that.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-12 mb-20">
          {POINTS.map((p, i) => (
            <motion.div
              key={p.title}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: duration.base, delay: i * stagger.tight }}
            >
              <h3 className="font-display text-lg mb-2" style={{ color: 'var(--text-primary)' }}>
                {p.title}
              </h3>
              <p className="text-sm leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
                {p.copy}
              </p>
            </motion.div>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-[minmax(0,340px)_1fr] gap-14 items-start pt-14 border-t" style={{ borderColor: 'var(--border)' }}>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: duration.base }}
          >
            <div className="rounded-2xl overflow-hidden border" style={{ borderColor: 'var(--border)' }}>
              <img
                src={founderPhoto}
                alt="Founder of Better Website Co."
                width={1024}
                height={1280}
                className="w-full h-auto object-cover"
              />
            </div>
            <div className="mt-5">
              <p className="font-display text-lg" style={{ color: 'var(--text-primary)' }}>
                Founder, Better Website Co.
              </p>
              <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>
                Designer • Developer • Strategist
              </p>
              <p className="eyebrow mt-3">Building digital first impressions that convert</p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: duration.base, delay: 0.1 }}
          >
            <p className="eyebrow mb-4">Meet the Founder</p>
            <div className="space-y-5 max-w-xl">
              <p className="font-display text-2xl md:text-3xl leading-snug" style={{ color: 'var(--text-primary)' }}>
                A website's job is to build trust, remove buying friction, and turn visitors
                into customers.
              </p>
              <p style={{ color: 'var(--text-secondary)' }}>
                That's what I set out to build with Better Website Co.
              </p>
              <p style={{ color: 'var(--text-secondary)' }}>
                Every project starts with your business, your customers, and your goals,
                before any template gets opened. The result loads fast, works on any device,
                and is built around getting visitors to say yes.
              </p>
              <p style={{ color: 'var(--text-primary)' }} className="font-medium">
                Whether you're launching your first business or rebuilding an established one,
                the goal is the same: a website customers remember, and one that gets them to
                say yes.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  )
}
